/**
 * Drives `/consent/agent-entered`.
 *
 * Nothing this hook sends is trusted: the server derives the advisor, the client and the copy
 * version from the token's own row, and compares both factors against stored values.
 *
 * The verify and consent steps are one mutation deliberately — splitting them would either
 * consume the token before the statements were accepted, or leave a verified-but-unconsented
 * state on the server for someone who closed the tab.
 */

import { useCallback, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AGENT_ENTERED_CONSENT_MAX_ATTEMPTS } from '@shared';

import { agentEnteredConsentApi } from '@client/api/waterlily';
import { captureConsentTokenOnce } from '@client/util/agentEnteredConsentToken';

export type AgentEnteredConsentState =
  | 'loading'
  /** No token in the link, or the server refused it: unknown, expired, cancelled. */
  | 'invalidLink'
  /** Token held and valid: show the email + date-of-birth form. */
  | 'verify'
  /** The details did not match. Same screen, with one generic message. */
  | 'verifyFailed'
  /** Verified and both boxes ticked: the submit is in flight. */
  | 'consenting'
  | 'consented'
  /** This link was already used — by this customer, on an earlier visit. */
  | 'alreadyConsented';

export interface AgentEnteredConsentValues {
  email: string;
  /** `YYYY-MM-DD`. */
  birthDate: string;
  acceptedPermission: boolean;
  acceptedAcknowledgement: boolean;
}

const EMPTY_VALUES: AgentEnteredConsentValues = {
  email: '',
  birthDate: '',
  acceptedPermission: false,
  acceptedAcknowledgement: false,
};

/**
 * The server answers every verification failure with 400 and one message, so the hook cannot —
 * and must not try to — tell a wrong date of birth from a lockout. 409 means the link was already
 * used, which is worth its own screen.
 */
function statusOf(error: unknown): number | undefined {
  const candidate = error as { status?: unknown; response?: { status?: unknown } } | null;
  const status = candidate?.response?.status ?? candidate?.status;
  return typeof status === 'number' ? status : undefined;
}

export function useAgentEnteredConsent() {
  const [token] = useState(() => captureConsentTokenOnce());
  const [values, setValues] = useState<AgentEnteredConsentValues>(EMPTY_VALUES);
  const [alreadyConsented, setAlreadyConsented] = useState(false);
  const [failedVerificationAttempts, setFailedVerificationAttempts] = useState(0);

  // A double click, or StrictMode's double effect run, must not send the token twice — the second
  // request would be refused and turn a success into an error screen.
  const hasSubmitted = useRef(false);

  const lookup = useQuery({
    queryKey: ['agentEnteredConsent', 'lookup', token],
    queryFn: () => agentEnteredConsentApi.lookup({ token: token! }),
    enabled: !!token,
    retry: false,
    // The answer cannot change while the page is open, and a refetch would be a wasted round
    // trip on a page the customer spends under a minute on.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  const submit = useMutation({
    mutationFn: (input: AgentEnteredConsentValues) =>
      agentEnteredConsentApi.submit({ token: token!, ...input }),
    onError: error => {
      const status = statusOf(error);
      if (status === 409) setAlreadyConsented(true);
      if (status === 400) {
        setFailedVerificationAttempts(current =>
          Math.min(current + 1, AGENT_ENTERED_CONSENT_MAX_ATTEMPTS),
        );
      }
      // A failed submit did not consume the token — the server gives it back when recording
      // fails, and a verification failure never consumed it — so release the single-submit guard.
      // The attempt limit below still prevents another verification request after the fifth one.
      hasSubmitted.current = false;
    },
  });

  const { mutate, reset: resetSubmit } = submit;
  const hasAttemptsRemaining = failedVerificationAttempts < AGENT_ENTERED_CONSENT_MAX_ATTEMPTS;
  const mustChangeIdentity = submit.isError && statusOf(submit.error) === 400;

  const setValue = useCallback(
    <K extends keyof AgentEnteredConsentValues>(key: K, value: AgentEnteredConsentValues[K]) => {
      setValues(current => ({ ...current, [key]: value }));

      const changedIdentityField = key === 'email' || key === 'birthDate';
      if (changedIdentityField && mustChangeIdentity && hasAttemptsRemaining) {
        resetSubmit();
      }
    },
    [hasAttemptsRemaining, mustChangeIdentity, resetSubmit],
  );

  const bothAccepted = values.acceptedPermission && values.acceptedAcknowledgement;
  const hasIdentity = values.email.trim().length > 0 && values.birthDate.trim().length > 0;
  const canSubmit =
    !!token &&
    hasIdentity &&
    bothAccepted &&
    hasAttemptsRemaining &&
    !mustChangeIdentity &&
    !submit.isPending;

  const confirm = useCallback(() => {
    if (!canSubmit || hasSubmitted.current) return;
    hasSubmitted.current = true;
    mutate(values);
  }, [canSubmit, mutate, values]);

  const state: AgentEnteredConsentState = useMemo(() => {
    if (!token) return 'invalidLink';
    if (lookup.isPending) return 'loading';
    if (lookup.isError) return 'invalidLink';
    if (lookup.data?.state === 'alreadyConsented' || alreadyConsented) return 'alreadyConsented';
    if (lookup.data?.state === 'invalid') return 'invalidLink';
    if (submit.isSuccess) return 'consented';
    if (submit.isPending) return 'consenting';
    if (submit.isError) return 'verifyFailed';
    return 'verify';
  }, [
    token,
    lookup.isPending,
    lookup.isError,
    lookup.data?.state,
    alreadyConsented,
    submit.isSuccess,
    submit.isPending,
    submit.isError,
  ]);

  return {
    state,
    values,
    setValue,
    canSubmit,
    confirm,
    /** One message for every way of failing to verify — the server gives no more than this. */
    errorMessage:
      state === 'verifyFailed'
        ? "We couldn't match those details. Please check the email address and date of birth and try again."
        : null,
    advisorFirstName: lookup.data?.advisorFirstName ?? '',
    advisorLastName: lookup.data?.advisorLastName ?? '',
    organizationDisplayName: lookup.data?.organizationDisplayName ?? '',
    clientFirstName: lookup.data?.clientFirstName ?? '',
    permissionText: lookup.data?.permissionText ?? '',
    acknowledgementText: lookup.data?.acknowledgementText ?? '',
  };
}
